/**
 * formatConditionConfig — pure formatter for ApplicabilityConstraint ADT.
 *
 * Maps the recursive ADT (dateBefore / dateOnOrAfter / equals / in /
 * and / or / not / alwaysTrue / …) to human-readable Polish text for
 * admin read-only display.
 *
 * Branches on `config.type` (discriminator field), not key presence.
 * Keeps parity with backend condition-config-formatter.ts.
 *
 * Safe fallback for unknown variants: returns
 *   "Warunek niestandardowy: <JSON>"
 * without crashing or producing [object Object].
 *
 * @see AC-4 in docs/specifications/2026-06-10-admin-pricing-composite-view/spec.md
 */

type ConstraintNode = {
  type: string;
  [key: string]: unknown;
};

/**
 * Format a conditionConfig ADT node to a Polish description string.
 * Returns empty string for null/undefined input.
 */
export function formatConditionConfig(
  config: ConstraintNode | Record<string, unknown> | null | undefined,
): string {
  if (!config) return "";

  // Treat as typed node only when `type` is a string
  const node = config as ConstraintNode;
  if (typeof node.type !== "string") {
    return `Warunek niestandardowy: ${JSON.stringify(config)}`;
  }

  try {
    switch (node.type) {
      case "alwaysTrue":
        return "Zawsze";

      case "equals": {
        const field = String(node.field ?? "");
        const value = String(node.value ?? "");
        return `${field} = ${value}`;
      }

      case "in": {
        const field = String(node.field ?? "");
        const values = Array.isArray(node.values)
          ? (node.values as unknown[]).map(String).join(", ")
          : "";
        return `${field} in [${values}]`;
      }

      case "dateBefore": {
        const field = String(node.field ?? "");
        const raw = String(node.value ?? "");
        const dateStr = raw ? new Date(raw).toLocaleDateString("pl-PL") : raw;
        return `${field} przed ${dateStr}`;
      }

      case "dateOnOrAfter": {
        const field = String(node.field ?? "");
        const raw = String(node.value ?? "");
        const dateStr = raw ? new Date(raw).toLocaleDateString("pl-PL") : raw;
        return `${field} od ${dateStr}`;
      }

      case "and": {
        const parts = Array.isArray(node.constraints)
          ? (node.constraints as ConstraintNode[])
              .map(formatConditionConfig)
              .filter(Boolean)
          : [];
        return `Wszystkie z: ${parts.join("; ")}`;
      }

      case "or": {
        const parts = Array.isArray(node.constraints)
          ? (node.constraints as ConstraintNode[])
              .map(formatConditionConfig)
              .filter(Boolean)
          : [];
        return `Jedno z: ${parts.join("; ")}`;
      }

      case "not": {
        const inner = formatConditionConfig(
          node.constraint as ConstraintNode | null,
        );
        return `Nie: ${inner}`;
      }

      default:
        // Safe fallback — avoids [object Object] for unknown ADT variants
        return `Warunek niestandardowy: ${JSON.stringify(config)}`;
    }
  } catch {
    // Defensive: any runtime error → safe fallback
    return `Warunek niestandardowy: ${JSON.stringify(config)}`;
  }
}
